import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Tabs } from 'antd';
import styled from 'styled-components';

import { getTemplateViews } from 'apis/ViewAPI';
import { useAppSelector } from 'store/hook';

import { VIEW_ADD_MODAL_TABS } from './constants';
import ConfirmModal from '../../../../common/Modal/ConfirmModal';
import Checkbox from '../../../lib/Checkbox';
import Input from '../../../lib/Input';
import { VIEW_CATEGORY, VIEW_MODALS, VIEW_MODALS_CONTENT } from '../../constants';
import ViewInputModalCommunityRentalTypes from '../ViewInputModal/ViewInputModalCommunityRentalTypes';

const StyledTabs = styled(Tabs)`
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
  gap: 10px;
  justify-content: space-around;
  flex-grow: 1;
`;

const Label = styled.span`
  font-weight: 900;
  min-width: 73px;
  font-size: 14px;
`;

const StyledCheckBox = styled(Checkbox)`
  min-width: 152px;

  .ant-checkbox + span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block; 
    width: 100%;
  }

  .ant-checkbox-indeterminate .ant-checkbox-inner:after {
    background-color: none;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-grow: 1;
`;

const TemplatesContainer = styled.div`
  margin-top: 10px;
  max-height: 230px;
  margin-bottom: 20px;
  overflow: auto;
  padding-right: 10px;
`;

const CheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmptyTemplatesContainer = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 30px;
  font-size: 10px;
  color: #000;
  opacity: 0.8;
`;

interface Props {
  open: boolean;
  type: string;
  loading: boolean;
  communityContractType: number;
  setCommunityContractType: (val: number) => void;
  canCreateFromTemplate?: boolean;
  createFromScratch: any;
  createFromTemplate: any;
  onCancel: any;
}
const AddViewTemplatesModal = ({
  open,
  type,
  loading,
  communityContractType,
  setCommunityContractType,
  canCreateFromTemplate = true,
  createFromTemplate,
  createFromScratch,
  onCancel,
}: Props) => {
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const [name, setName] = useState<string>('');

  const [selectedCheckboxIds, setSelectedCheckboxIds] = useState<any[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const [activeTabKey, setActiveTabKey] = useState(VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.key);

  useEffect(() => {
    if (!canCreateFromTemplate && activeTabKey === VIEW_ADD_MODAL_TABS.CREATE_FROM_TEMPLATE.key) {
      setActiveTabKey(VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.key);
    }
  }, [canCreateFromTemplate, activeTabKey]);

  const { data } = useQuery({
    queryKey: ['template', 'views', selectedCommunity?.id],
    queryFn: () => getTemplateViews(selectedCommunity?.id ?? -1),
    enabled: Boolean(selectedCommunity?.id),
  });

  const templateViews = data?.length ? data.filter((view: any) => view.category === VIEW_CATEGORY.CUSTOM) : [];

  const handleConfirm = () => {
    if (activeTabKey === VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.key) {
      createFromScratch(name).then(() => {
        handleCancel();
      });
    } else if (canCreateFromTemplate && activeTabKey === VIEW_ADD_MODAL_TABS.CREATE_FROM_TEMPLATE.key) {
      const views = selectedCheckboxIds.map((id) => ({
        id,
        name: inputValues[id],
      }));
      createFromTemplate(views).then(() => {
        handleCancel();
      });
    }
  };

  const handleChange = (e: any) => {
    setName(e.target.value);
  };

  const handleCancel = () => {
    setName('');
    setSelectedCheckboxIds([]);
    setInputValues({});
    setActiveTabKey(VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.key);
    onCancel();
  };

  const handleCheckboxChange = (idsList: any[]) => {
    setSelectedCheckboxIds(idsList);

    const updatedInputValues = { ...inputValues };
    idsList.forEach((id) => {
      if (!updatedInputValues[id]) {
        const view = templateViews?.find((v: any) => v.id === id);
        if (view) {
          updatedInputValues[id] = view.name;
        }
      }
    });
    setInputValues(updatedInputValues);
  };

  const handleInputChange = (id: string, value: string) => {
    setInputValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      const allIds = templateViews.map((v: any) => v.id) || [];
      setSelectedCheckboxIds(allIds);

      const allInputValues: Record<string, string> = {};
      templateViews.forEach((v: any) => {
        allInputValues[v.id] = v.name;
      });
      setInputValues(allInputValues);
    } else {
      setSelectedCheckboxIds([]);
      setInputValues({});
    }
  };

  const isCreateFromScratch = activeTabKey === VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.key;
  const isCreateFromTemplate = activeTabKey === VIEW_ADD_MODAL_TABS.CREATE_FROM_TEMPLATE.key;

  const isScratchInvalid = isCreateFromScratch && name.trim().length === 0;

  const isTemplateInvalid =
    isCreateFromTemplate &&
    (selectedCheckboxIds.length === 0 ||
      selectedCheckboxIds.some((id) => !inputValues[id] || inputValues[id].trim() === ''));

  const isSaveDisabled =
    type !== VIEW_MODALS.DELETE && type !== VIEW_MODALS.RESET_LAYOUT && (isScratchInvalid || isTemplateInvalid);

  return (
    <ConfirmModal
      open={open}
      title={VIEW_MODALS_CONTENT[type].title}
      description={VIEW_MODALS_CONTENT[type].description}
      confirmLabel={VIEW_MODALS_CONTENT[type].label}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      loading={loading}
      disabled={isSaveDisabled}
    >
      <StyledTabs activeKey={activeTabKey} onChange={(key) => setActiveTabKey(key)}>
        <Tabs.TabPane
          tab={VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.title}
          key={VIEW_ADD_MODAL_TABS.CREATE_FROM_SCRATCH.key}
        >
          <ViewInputModalCommunityRentalTypes
            communityContractType={communityContractType}
            setCommunityContractType={setCommunityContractType}
          />
          <InputContainer>
            <Label>View Name</Label>
            <Input placeholder="Enter a name" onChange={handleChange} />
          </InputContainer>
        </Tabs.TabPane>
        {canCreateFromTemplate && (
          <Tabs.TabPane
            tab={VIEW_ADD_MODAL_TABS.CREATE_FROM_TEMPLATE.title}
            key={VIEW_ADD_MODAL_TABS.CREATE_FROM_TEMPLATE.key}
          >
            {!templateViews.length ? (
              <EmptyTemplatesContainer>No Templates Found</EmptyTemplatesContainer>
            ) : (
              <span>
                <Container>
                  <StyledCheckBox
                    className={'green'}
                    checked={selectedCheckboxIds.length === templateViews.length}
                    indeterminate={selectedCheckboxIds.length > 0 && selectedCheckboxIds.length < templateViews}
                    onChange={handleSelectAll}
                  >
                    <Label>Template</Label>
                  </StyledCheckBox>
                  <Label>View Name</Label>
                </Container>

                <TemplatesContainer className="mini-scroll">
                  <CheckboxGroup value={selectedCheckboxIds} onChange={handleCheckboxChange}>
                    {templateViews.map((view: any) => (
                      <Container key={view.id}>
                        <StyledCheckBox value={view.id} className={'green'}>
                          {view.name}
                        </StyledCheckBox>
                        <Input
                          placeholder={view.name}
                          value={inputValues[view.id] || ''}
                          onChange={(e: any) => handleInputChange(view.id, e.target.value)}
                          disabled={!selectedCheckboxIds.includes(view.id)}
                        />
                      </Container>
                    ))}
                  </CheckboxGroup>
                </TemplatesContainer>
              </span>
            )}
          </Tabs.TabPane>
        )}
      </StyledTabs>
    </ConfirmModal>
  );
};

export default AddViewTemplatesModal;
